import type { Middleware } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';
import { map, sum, maxBy, values } from 'lodash-es';
import type { CartItem } from '../../types';
import { addItem, addAddon, addPart, addPartAddon, removeItem, removeItemByUUID, resetParts } from '../slices/cartSlice';
import type { RootState } from '../index';

// Action types for raw (un-enhanced) cart operations
const RAW_ADD_ITEM = 'cart/rawAddItem';
const RAW_ADD_ADDON = 'cart/rawAddAddon';
const RAW_ADD_PART = 'cart/rawAddPart';
const RAW_ADD_PART_ADDON = 'cart/rawAddPartAddon';
const RAW_ADD_LAST_PART = 'cart/rawAddLastPart';
const RAW_REMOVE_ITEM_WITH_SIBLINGS = 'cart/rawRemoveItemWithSiblings';

// Action creators for raw operations (dispatched from UI)
export const rawAddItem = (item: any) => ({ type: RAW_ADD_ITEM, payload: item });
export const rawAddAddon = (item: any, targetUUID: string) => ({ type: RAW_ADD_ADDON, payload: item, meta: { target: targetUUID } });
export const rawAddPart = (item: any) => ({ type: RAW_ADD_PART, payload: item });
export const rawAddPartAddon = (item: any, targetUUID: string) => ({ type: RAW_ADD_PART_ADDON, payload: item, meta: { target: targetUUID } });
export const rawAddLastPart = (item: any, previousParts: Record<string, CartItem>) => ({ type: RAW_ADD_LAST_PART, payload: item, meta: { previous_parts: previousParts } });
export const rawRemoveItemWithSiblings = (item: CartItem) => ({ type: RAW_REMOVE_ITEM_WITH_SIBLINGS, payload: item });

function enhanceItem(item: any): CartItem {
  return {
    uuid: uuid(),
    gratis: false,
    total_price: item.price,
    main_part: true,
    ...item,
  };
}

export const cartMiddleware: Middleware = (storeApi) => (next) => (action: any) => {
  const { dispatch } = storeApi;

  switch (action.type) {
    case RAW_ADD_ITEM: {
      const enhanced = enhanceItem(action.payload);
      dispatch(addItem(enhanced));
      return;
    }

    case RAW_ADD_ADDON: {
      const enhanced = enhanceItem(action.payload);
      dispatch(addAddon({ item: enhanced, targetUUID: action.meta.target }));
      return;
    }

    case RAW_ADD_PART: {
      const enhanced = enhanceItem(action.payload);
      dispatch(addPart(enhanced));
      return;
    }

    case RAW_ADD_PART_ADDON: {
      const enhanced = enhanceItem(action.payload);
      dispatch(addPartAddon({ item: enhanced, targetUUID: action.meta.target }));
      return;
    }

    case RAW_ADD_LAST_PART: {
      const lastPart = enhanceItem(action.payload);
      const previousParts = action.meta.previous_parts as Record<string, CartItem>;
      // Clone each part — previousParts are frozen Redux state objects
      const allParts: Record<string, CartItem> = {};
      Object.entries(previousParts).forEach(([key, part]) => {
        allParts[key] = { ...part };
      });
      allParts[lastPart.uuid] = lastPart;
      const siblingList = Object.keys(allParts);
      const totalPrice = sum(map(allParts, (part) => part.price));
      const mostExpensiveUUID = maxBy(values(allParts), 'price')?.uuid;

      if (lastPart.price_algorithm_max) {
        Object.values(allParts).forEach((part) => {
          if (part.uuid !== mostExpensiveUUID) {
            part.gratis = true;
            part.main_part = false;
          }
        });
      } else {
        Object.values(allParts).forEach((part) => {
          if (part.uuid !== mostExpensiveUUID) {
            part.main_part = false;
          }
          part.total_price = totalPrice;
        });
      }

      // Dispatch all parts with sibling list to cart
      Object.values(allParts).forEach((part) => {
        dispatch(addItem({ ...part, sibling_list: siblingList }));
      });
      dispatch(resetParts());
      return;
    }

    case RAW_REMOVE_ITEM_WITH_SIBLINGS: {
      const item = action.payload as CartItem;
      if (item.sibling_list) {
        item.sibling_list.forEach((siblingUUID) => {
          dispatch(removeItemByUUID(siblingUUID));
        });
      }
      dispatch(removeItem(item.uuid));
      return;
    }

    default:
      return next(action);
  }
};
